import { useCallback, useMemo, useRef, useState } from 'react'
import {
  buildAcceptedStandardReferenceSet,
  filterRerunReviewCandidates,
} from '@artem-lex/common-server/standards/standardApplicabilityReference'
import type { RerunApplicabilityCandidate } from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  useCommitStandardApplicabilityRerun,
  useListStandardApplicability,
  usePreviewTypeBQuestionnaireCandidates,
  useStartMachineCharacteristicsExtractionDryRun,
  useStartTypeCStandardApplicabilityCheckDryRun,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import type { WorkflowEvent } from '@/shared/hooks/api/workflows'
import {
  getInitialMachineCharacteristicsExtractionTaskState,
  reduceMachineCharacteristicsExtractionTaskState,
  type MachineCharacteristicsExtractionTaskState,
} from '../../utils/machineCharacteristicsExtractionTaskState'
import {
  getInitialTypeCStandardCheckTaskState,
  reduceTypeCStandardCheckTaskState,
  type TypeCStandardCheckTaskState,
} from '../../utils/typeCStandardCheckTaskState'

export type RerunSheetStep =
  | 'scope'
  | 'typeCProgress'
  | 'typeCReview'
  | 'typeBExtractProgress'
  | 'typeBQuestionnaire'
  | 'typeBReview'
  | 'finalReview'

export type RerunDraftTypeC = {
  candidates: RerunApplicabilityCandidate[]
  selectedReferences: Set<string>
  cleanupOnly: boolean
}

export type RerunDraftTypeB = {
  answersJson: Record<string, unknown>
  evidenceJson: Record<string, unknown>
  extractionSkipped: boolean
  templateVersion: string
  mappingVersion: string
  applicableQuestionKeys: string[]
  generationFingerprint: string
  candidates: RerunApplicabilityCandidate[]
  selectedReferences: Set<string>
  cleanupOnly: boolean
}

export type RerunStandardApplicabilityDraft = {
  runTypeC: boolean
  runTypeB: boolean
  typeC?: RerunDraftTypeC
  typeB?: RerunDraftTypeB
}

export function useRerunStandardApplicabilitySheet(complianceId: string) {
  const [step, setStep] = useState<RerunSheetStep>('scope')
  const [scopeTypeC, setScopeTypeC] = useState(false)
  const [scopeTypeB, setScopeTypeB] = useState(false)
  const [draft, setDraft] = useState<RerunStandardApplicabilityDraft | null>(
    null
  )
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [typeCProgressState, setTypeCProgressState] =
    useState<TypeCStandardCheckTaskState>(getInitialTypeCStandardCheckTaskState())
  const [typeBExtractProgressState, setTypeBExtractProgressState] =
    useState<MachineCharacteristicsExtractionTaskState>(
      getInitialMachineCharacteristicsExtractionTaskState()
    )

  const { data: applicabilityStandards = [] } =
    useListStandardApplicability(complianceId)
  const acceptedReferenceSet = useMemo(
    () =>
      buildAcceptedStandardReferenceSet(
        applicabilityStandards
          .filter((standard) => standard.isAccepted === true)
          .map((standard) => standard.reference)
      ),
    [applicabilityStandards]
  )
  const filterReviewCandidates = useCallback(
    (candidates: RerunApplicabilityCandidate[]) =>
      filterRerunReviewCandidates(candidates, acceptedReferenceSet),
    [acceptedReferenceSet]
  )

  const confirmedRef = useRef(false)
  const typeCDryRun = useStartTypeCStandardApplicabilityCheckDryRun(complianceId)
  const extractDryRun =
    useStartMachineCharacteristicsExtractionDryRun(complianceId)
  const previewTypeB = usePreviewTypeBQuestionnaireCandidates(complianceId)
  const commitMutation = useCommitStandardApplicabilityRerun(complianceId)

  const reset = useCallback(() => {
    setStep('scope')
    setScopeTypeC(false)
    setScopeTypeB(false)
    setDraft(null)
    confirmedRef.current = false
    setError(null)
    setTypeCProgressState(getInitialTypeCStandardCheckTaskState())
    setTypeBExtractProgressState(getInitialMachineCharacteristicsExtractionTaskState())
  }, [])

  const cancelActiveWorkflows = useCallback(async () => {
    await Promise.all([typeCDryRun.cancel(), extractDryRun.cancel()])
  }, [extractDryRun, typeCDryRun])

  const confirmScope = useCallback(() => {
    if (!scopeTypeC && !scopeTypeB) {
      return
    }
    confirmedRef.current = true
    setDraft({ runTypeC: scopeTypeC, runTypeB: scopeTypeB })
    setError(null)
    if (scopeTypeC) {
      setStep('typeCProgress')
    } else {
      setStep('typeBExtractProgress')
    }
  }, [scopeTypeB, scopeTypeC])

  const runTypeCProgress = useCallback(async () => {
    setError(null)
    setTypeCProgressState(getInitialTypeCStandardCheckTaskState())
    try {
      const candidates = filterReviewCandidates(
        await typeCDryRun.mutateAsync({
          onEvent: (event: WorkflowEvent) => {
            setTypeCProgressState((previous) =>
              reduceTypeCStandardCheckTaskState(previous, event)
            )
          },
        })
      )
      setDraft((previous) => {
        if (previous == null) {
          return previous
        }
        const selected = new Set<string>()
        return {
          ...previous,
          typeC: {
            candidates,
            selectedReferences: selected,
            cleanupOnly: candidates.length === 0,
          },
        }
      })
      setStep('typeCReview')
    } catch (runError) {
      setError(
        runError instanceof Error
          ? runError.message
          : 'Type C applicability check failed'
      )
    }
  }, [filterReviewCandidates, typeCDryRun])

  const continueFromTypeCReview = useCallback(() => {
    if (draft?.runTypeB) {
      setStep('typeBExtractProgress')
      return
    }
    setStep('finalReview')
  }, [draft?.runTypeB])

  const runTypeBExtractProgress = useCallback(async () => {
    setError(null)
    setTypeBExtractProgressState(getInitialMachineCharacteristicsExtractionTaskState())
    try {
      const result = await extractDryRun.mutateAsync({
        onEvent: (event: WorkflowEvent) => {
          setTypeBExtractProgressState((previous) =>
            reduceMachineCharacteristicsExtractionTaskState(previous, event)
          )
        },
      })
      if ('skipped' in result && result.skipped === true) {
        if (result.reason === 'cancelled') {
          return
        }
        setDraft((previous) => {
          if (previous == null) {
            return previous
          }
          return {
            ...previous,
            typeB: {
              answersJson: {},
              evidenceJson: {},
              extractionSkipped: true,
              templateVersion: '',
              mappingVersion: '',
              applicableQuestionKeys: [],
              generationFingerprint: '',
              candidates: [],
              selectedReferences: new Set(),
              cleanupOnly: true,
            },
          }
        })
        setStep('typeBQuestionnaire')
        return
      }
      if (!('dryRun' in result) || result.dryRun !== true) {
        throw new Error('Unexpected machine characteristics extraction result')
      }
      const extracted = result
      setDraft((previous) => {
        if (previous == null) {
          return previous
        }
        return {
          ...previous,
          typeB: {
            answersJson: extracted.answers,
            evidenceJson: extracted.evidence,
            extractionSkipped: false,
            templateVersion: extracted.templateVersion,
            mappingVersion: extracted.mappingVersion,
            applicableQuestionKeys: extracted.applicableQuestionKeys,
            generationFingerprint: '',
            candidates: [],
            selectedReferences: new Set(),
            cleanupOnly: false,
          },
        }
      })
      setStep('typeBQuestionnaire')
    } catch (runError) {
      setError(
        runError instanceof Error
          ? runError.message
          : 'Machine characteristics extraction failed'
      )
    }
  }, [extractDryRun])

  const generateTypeBPreview = useCallback(async (
    answersOverride?: Record<string, unknown>
  ) => {
    if (draft?.typeB == null) {
      return
    }
    setError(null)
    const answers = answersOverride ?? draft.typeB.answersJson
    if (answersOverride != null) {
      setDraft((previous) => {
        if (previous?.typeB == null) {
          return previous
        }
        return {
          ...previous,
          typeB: { ...previous.typeB, answersJson: answersOverride },
        }
      })
    }
    try {
      const preview = await previewTypeB.mutateAsync({
        answers,
      })
      const candidates = filterReviewCandidates(preview.candidates)
      setDraft((previous) => {
        if (previous?.typeB == null) {
          return previous
        }
        return {
          ...previous,
          typeB: {
            ...previous.typeB,
            templateVersion: preview.completion.templateVersion,
            mappingVersion: preview.completion.mappingVersion,
            applicableQuestionKeys: preview.completion.visibleQuestionKeys,
            generationFingerprint: preview.generationFingerprint,
            candidates,
            selectedReferences: new Set(),
            cleanupOnly: candidates.length === 0,
          },
        }
      })
      setStep('typeBReview')
    } catch (runError) {
      setError(
        runError instanceof Error
          ? runError.message
          : 'Type B candidate generation failed'
      )
    }
  }, [draft?.typeB, filterReviewCandidates, previewTypeB])

  const continueFromTypeBReview = useCallback(() => {
    setStep('finalReview')
  }, [])

  const acceptCommit = useCallback(async () => {
    if (draft == null) {
      return
    }
    setError(null)
    const payload: Parameters<typeof commitMutation.mutateAsync>[0] = {}
    if (draft.runTypeC && draft.typeC != null) {
      if (draft.typeC.cleanupOnly) {
        payload.typeC = { cleanupOnly: true }
      } else {
        payload.typeC = {
          candidates: draft.typeC.candidates,
          selectedReferences: [...draft.typeC.selectedReferences],
        }
      }
    }
    if (draft.runTypeB && draft.typeB != null) {
      if (draft.typeB.cleanupOnly) {
        payload.typeB = { cleanupOnly: true }
      } else {
        payload.typeB = {
          answersJson: draft.typeB.answersJson,
          evidenceJson: draft.typeB.evidenceJson,
          templateVersion: draft.typeB.templateVersion,
          mappingVersion: draft.typeB.mappingVersion,
          applicableQuestionKeys: draft.typeB.applicableQuestionKeys,
          generationFingerprint: draft.typeB.generationFingerprint,
          candidates: draft.typeB.candidates,
          selectedReferences: [...draft.typeB.selectedReferences],
        }
      }
    }
    try {
      await commitMutation.mutateAsync(payload)
    } catch (commitError) {
      setError(
        commitError instanceof Error
          ? commitError.message
          : 'Failed to commit standard applicability rerun'
      )
      throw commitError
    }
  }, [commitMutation, draft])

  const requestLeave = useCallback(() => {
    if (!confirmedRef.current) {
      return true
    }
    setLeaveConfirmOpen(true)
    return false
  }, [])

  const confirmLeave = useCallback(async () => {
    await cancelActiveWorkflows()
    setLeaveConfirmOpen(false)
    reset()
  }, [cancelActiveWorkflows, reset])

  const updateTypeCSelection = useCallback(
    (selectedReferences: Set<string>) => {
      setDraft((previous) => {
        if (previous?.typeC == null) {
          return previous
        }
        return {
          ...previous,
          typeC: { ...previous.typeC, selectedReferences },
        }
      })
    },
    []
  )

  const updateTypeBAnswers = useCallback(
    (answersJson: Record<string, unknown>) => {
      setDraft((previous) => {
        if (previous?.typeB == null) {
          return previous
        }
        return {
          ...previous,
          typeB: { ...previous.typeB, answersJson },
        }
      })
    },
    []
  )

  const updateTypeBSelection = useCallback(
    (selectedReferences: Set<string>) => {
      setDraft((previous) => {
        if (previous?.typeB == null) {
          return previous
        }
        return {
          ...previous,
          typeB: { ...previous.typeB, selectedReferences },
        }
      })
    },
    []
  )

  return {
    step,
    scopeTypeC,
    setScopeTypeC,
    scopeTypeB,
    setScopeTypeB,
    draft,
    error,
    leaveConfirmOpen,
    setLeaveConfirmOpen,
    typeCProgressState,
    typeBExtractProgressState,
    confirmScope,
    runTypeCProgress,
    continueFromTypeCReview,
    runTypeBExtractProgress,
    generateTypeBPreview,
    continueFromTypeBReview,
    acceptCommit,
    requestLeave,
    confirmLeave,
    reset,
    updateTypeCSelection,
    updateTypeBAnswers,
    updateTypeBSelection,
    isTypeCRunning: typeCDryRun.isPending,
    isTypeBPreviewRunning: previewTypeB.isPending,
    isCommitting: commitMutation.isPending,
  }
}
